import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { styled } from '../../system';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Label } from '@radix-ui/react-label';
export const Input = forwardRef(({ as, css, className, label, helpText, readOnly, prefix, prefixBorder, prefixCSS, suffix, suffixBorder, suffixCSS, onChange, onFocus, onBlur, statusText, disabled = false, type = 'text', size = 'lg', status = 'default', ...props }, ref) => {
    const inputRef = useRef(null);
    // @ts-ignore
    useImperativeHandle(ref, () => inputRef.current);
    const [focus, setFocus] = useState(false);
    const isTextarea = useMemo(() => as === 'textarea', [as]);
    const handleChange = (event) => {
        if (disabled || readOnly) {
            return;
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(event);
    };
    const handleFocus = (e) => {
        setFocus(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    };
    const handleBlur = (e) => {
        setFocus(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    };
    const hasPrefix = useMemo(() => Boolean(prefix && !isTextarea), [prefix, isTextarea]);
    const hasSuffix = useMemo(() => Boolean(suffix && !isTextarea), [suffix, isTextarea]);
    const state = useMemo(() => (focus ? 'focus' : disabled ? 'disabled' : undefined), [focus, disabled]);
    return (_jsxs(StyledFieldContainer, { as: "label", css: css, className: className, children: [label && _jsx(StyledLabel, { children: label }), _jsxs(StyledInputContainer, { size: size, state: state, status: status, textarea: isTextarea, children: [hasPrefix && (_jsx(StyledAddon, { css: prefixCSS, hasBorder: prefixBorder, position: "start", children: prefix })), _jsx(StyledInput, { ...props, ref: inputRef, as: as, disabled: disabled, hasPrefix: hasPrefix, hasSuffix: hasSuffix, size: size, status: status, textarea: isTextarea, type: type, onBlur: handleBlur, onChange: handleChange, onFocus: handleFocus }), hasSuffix && (_jsx(StyledAddon, { css: suffixCSS, hasBorder: suffixBorder, position: "end", children: suffix }))] }), (helpText || statusText) && (_jsx(StyledHelpText, { status: status, children: statusText && status ? statusText : helpText }))] }));
});
const StyledInput = styled('input', {
    // Reset
    appearance: 'none',
    borderWidth: 0,
    boxSizing: 'border-box',
    margin: 0,
    outline: 'none',
    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
    '&::before': {
        boxSizing: 'border-box'
    },
    '&::after': {
        boxSizing: 'border-box'
    },
    // Custom
    px: 14,
    fontVariantNumeric: 'tabular-nums',
    '&:-webkit-autofill': {
        boxShadow: 'none'
    },
    '&:-webkit-autofill::first-line': {
        fontFamily: '$sans',
        color: '$inputText'
    },
    '&::placeholder': {
        color: '$inputPlaceholder'
    },
    fontFamily: '$sans',
    fontWeight: '$regular',
    lineHeight: '$none',
    color: '$inputText',
    bgColor: 'transparent',
    h: '$full',
    flex: 1,
    variants: {
        textarea: {
            true: {
                px: 14,
                py: 10,
                h: '$full',
                minH: '$inputHeight',
                textMd: true
            }
        },
        hasPrefix: {
            true: {
                pl: 0
            }
        },
        hasSuffix: {
            true: {
                pr: 0
            }
        },
        status: {
            default: {},
            error: {},
            warning: {},
            success: {}
        }
    }
});
export const StyledFieldContainer = styled(Label, {
    display: 'inline-flex',
    flexDir: 'column',
    lineHeight: '$none'
});
const StyledInputContainer = styled('div', {
    d: 'flex',
    position: 'relative',
    color: '$inputText',
    bgColor: '$inputBg',
    border: '1px solid $inputBorder',
    rounded: '$inputRadii',
    shadow: '$xs',
    overflow: 'hidden',
    variants: {
        status: {
            default: {
                borderColor: '$inputBorder'
            },
            error: {
                borderColor: '$errorBorder'
            },
            warning: {
                borderColor: '$warningBorder'
            },
            success: {
                borderColor: '$successBorder'
            }
        },
        size: {
            sm: {
                minMaxH: '$inputSmHeight',
                '&, & input': {
                    textSm: true,
                    fontSize: '$inputSmFontSize',
                    fontWeight: '$inputSmFontWeight'
                }
            },
            md: {
                minMaxH: '$inputMdHeight',
                '&, & input': {
                    textSm: true,
                    fontSize: '$inputMdFontSize',
                    fontWeight: '$inputMdFontWeight'
                }
            },
            lg: {
                minMaxH: '$inputLgHeight',
                '&, & input': {
                    textMd: true,
                    fontSize: '$inputLgFontSize',
                    fontWeight: '$inputLgFontWeight'
                }
            },
            xl: {
                minMaxH: '$inputXlHeight',
                '&, & input': {
                    textMd: true,
                    fontSize: '$inputXlFontSize',
                    fontWeight: '$inputXlFontWeight'
                }
            },
            '2xl': {
                minMaxH: '$input2XlHeight',
                '&, & input': {
                    textLg: true,
                    fontSize: '$input2XlFontSize',
                    fontWeight: '$input2XlFontWeight'
                }
            }
        },
        state: {
            focus: {
                shadow: '$focusRingPrimary',
                color: '$inputTextFocus',
                bgColor: '$inputBgFocus',
                borderColor: '$inputBorderFocus'
            },
            disabled: {
                opacity: 0.8,
                color: '$inputTextDisabled',
                bgColor: '$inputBgDisabled',
                borderColor: '$inputBorderDisabled'
            }
        },
        textarea: {
            true: {
                px: 0,
                h: 'auto',
                minH: '$inputHeight',
                maxH: 'none'
            }
        }
    },
    compoundVariants: [
        {
            state: 'focus',
            status: 'error',
            css: {
                shadow: '$focusRingDanger !important',
                borderColor: '$errorBorder !important'
            }
        },
        {
            state: 'focus',
            status: 'warning',
            css: {
                shadow: '$focusRingWarning !important',
                borderColor: '$warningBorder !important'
            }
        },
        {
            state: 'focus',
            status: 'success',
            css: {
                shadow: '$focusRingSuccess !important',
                borderColor: '$successBorder !important'
            }
        }
    ]
});
export const StyledLabel = styled('span', {
    fontFamily: '$sans',
    fontWeight: '$medium',
    textSm: true,
    mb: '$2',
    color: '$text'
});
export const StyledHelpText = styled('span', {
    fontFamily: '$sans',
    fontWeight: '$regular',
    textSm: true,
    mt: '$2',
    variants: {
        state: {
            default: {
                color: '$textMuted'
            }
        },
        status: {
            default: {
                color: '$textMuted'
            },
            error: {
                color: '$errorText'
            },
            warning: {
                color: '$warningText'
            },
            success: {
                color: '$successText'
            }
        }
    },
    defaultVariants: {
        state: 'default'
    }
});
const StyledAddon = styled('span', {
    h: '$full',
    d: 'inline-flex',
    alignItems: 'center',
    px: 14,
    lineHeight: '$none',
    variants: {
        position: {
            start: {},
            end: {}
        },
        disabled: {
            true: {
                color: '$textMuted',
                bgColor: '$neutral50'
            }
        },
        hasBorder: {
            true: {
                color: '$textMuted'
            }
        }
    },
    compoundVariants: [
        {
            hasBorder: true,
            position: 'start',
            css: {
                br: '1px solid $inputBorder',
                mr: 14
            }
        },
        {
            hasBorder: true,
            position: 'end',
            css: {
                bl: '1px solid $inputBorder',
                ml: 14
            }
        },
        {
            hasBorder: true,
            css: {
                bgColor: '$inputAddonBg'
            }
        },
        {
            hasBorder: true,
            disabled: true,
            css: {
                bgColor: '$inputAddonBg'
            }
        }
    ]
});
// @ts-ignore
Input.displayName = 'Input';
